<template>
  <div class="fixed bottom-24 right-2 flex flex-col items-end gap-1 pointer-events-none select-none" style="z-index: 55;">

    <!-- Expanded debug panel — expands upward above the toggle button -->
    <div v-if="showPanel"
         class="w-56 rounded-lg p-2 text-white pointer-events-none"
         style="background: rgba(0,0,0,0.55); font-family: monospace; font-size: 10px; line-height: 1.35;">

      <!-- Status flags -->
      <div v-if="isOverlayVisible || isPreviewOpen || accuracyBlocked" class="flex flex-wrap gap-1 mb-1">
        <button v-if="isOverlayVisible" class="bg-gray-200 text-black px-1 rounded shadow pointer-events-auto" @click="onCloseOverlay && onCloseOverlay()">close overlay</button>
        <span v-if="isPreviewOpen"    class="bg-yellow-600 px-1 rounded">preview</span>
        <span v-if="accuracyBlocked"  class="bg-red-700   px-1 rounded">acc.blocked</span>
      </div>

      <!-- History count & zoom -->
      <div class="text-gray-400 mb-1 flex items-baseline justify-between">
        <span>{{ historyCount }}/5 readings</span>
        <span>zoom: <b :class="nearbyZoomBoost > 0 ? 'text-cyan-300' : ''">{{ mapZoomLevel }}</b><template v-if="nearbyZoomBoost > 0"> +{{ nearbyZoomBoost }}</template></span>
      </div>

      <!-- Nearest point detail -->
      <template v-if="nearestPoint">
        <div class="border-t border-white border-opacity-20 pt-1">
          <div class="font-bold text-yellow-300 truncate" :title="nearestPoint.name">{{ nearestPoint.name }}</div>
          <div class="flex flex-wrap gap-x-2 mt-0.5">
            <span>avg: <b>{{ Math.round(nearestPoint.avgDistance) }}m</b></span>
            <span v-if="nearestPoint.gpsOpen">open-r: <b>{{ Math.round(nearestPoint.effOpenRadius) }}m</b></span>
            <span v-if="nearestPoint.gpsLock">unlock-r: <b>{{ Math.round(nearestPoint.effUnlockRadius) }}m</b></span>
          </div>
          <div class="flex flex-wrap gap-1 mt-0.5">
            <span v-if="nearestPoint.gpsOpen"
                  class="px-1 rounded"
                  :class="nearestPoint.inAutoOpenRange ? 'bg-green-700' : 'bg-gray-700'">
              auto: {{ nearestPoint.inAutoOpenRange ? '✓' : '✗' }}
            </span>
            <span v-if="nearestPoint.gpsLock"
                  class="px-1 rounded"
                  :class="!nearestPoint.isLocked ? 'bg-green-700' : 'bg-gray-700'">
              lock: {{ nearestPoint.isLocked ? 'locked' : 'open' }}
            </span>
            <span v-if="nearestPoint.alreadyOpened" class="bg-orange-700 px-1 rounded">opened</span>
          </div>
          <!-- Unmet game conditions -->
          <template v-if="nearestPoint.unmetConditions && nearestPoint.unmetConditions.length > 0">
            <div class="text-red-300 mt-0.5">
              <div>unmet:</div>
              <div v-for="(cond, i) in nearestPoint.unmetConditions" :key="i" class="pl-1">
                · {{ describeCondition(cond) }}
              </div>
            </div>
          </template>
          <div v-else-if="nearestPoint.gameActive" class="text-green-400 mt-0.5">cond: all met</div>
        </div>
      </template>
      <div v-else class="text-gray-400">no gps-enabled points</div>
    </div>

    <!-- Toggle button — always visible, clickable -->
    <button
      class="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-white font-mono pointer-events-auto"
      style="font-size: 11px; background: rgba(0,0,0,0.60); border: none; cursor: pointer;"
      @click="showPanel = !showPanel"
    >
      <!-- Accuracy dot -->
      <span class="w-2 h-2 rounded-full flex-shrink-0" :style="{ backgroundColor: accuracyColor }"></span>
      <!-- Accuracy value -->
      <span>{{ gpsAccuracy != null ? '±' + Math.round(gpsAccuracy) + 'm' : 'GPS –' }}</span>
      <!-- Distance to nearest point -->
      <span v-if="nearestPoint" class="text-gray-300">·</span>
      <span v-if="nearestPoint">{{ Math.round(nearestPoint.avgDistance) }}m</span>
    </button>

  </div>
</template>

<script>
import moduleManager from '../modules/index.js'
import { showMultiLingualText } from '../utils/languageUtils'

export default {
  name: 'DebugGPSPanel',
  props: {
    fetchedPoints:      { type: Object,  default: () => ({}) },
    visiblePoints:      { type: Object,  default: () => ({}) },
    currentLocation:    { type: Object,  default: null },
    gpsAccuracy:        { type: Number,  default: null },
    locationHistory:    { type: Array,   default: () => [] },
    gpsAutoOpenedPoints:{ type: Object,  default: () => new Set() },
    gpsLockedPoints:    { type: Object,  default: () => new Set() },
    gpsOpenRadius:      { type: Number,  default: 5 },
    gpsUnlockRadius:    { type: Number,  default: 15 },
    nearbyZoomBoost:    { type: Number,  default: 0 },
    mapZoom:            { type: Number,  default: null },
    isOverlayVisible:   { type: Boolean, default: false },
    isPreviewOpen:      { type: Boolean, default: false },
    onCloseOverlay:     { type: Function, default: null }
  },
  data() {
    return {
      showPanel: false
    }
  },
  computed: {
    historyCount() {
      return this.locationHistory.length
    },

    mapZoomLevel() {
      return this.mapZoom != null ? this.mapZoom : '–'
    },

    accuracyBlocked() {
      const maxAccuracy = parseFloat(process.env.GPS_MAX_ACCURACY) || 50
      return this.gpsAccuracy !== null && this.gpsAccuracy > maxAccuracy
    },

    accuracyColor() {
      const a = this.gpsAccuracy
      if (a == null) return '#9ca3af'
      if (a < 5)   return '#22c55e'
      if (a < 10)  return '#84cc16'
      if (a < 20)  return '#facc15'
      if (a < 50)  return '#fb923c'
      if (a < 100) return '#ea580c'
      return '#ef4444'
    },

    nearestPoint() {
      if (!this.currentLocation) return null

      const history = this.locationHistory.length > 0 ? this.locationHistory : [this.currentLocation]
      const gameActive = moduleManager.isModuleActive('game')
      const gameModule = gameActive ? moduleManager.getModule('game') : null

      let best = null

      Object.values(this.fetchedPoints).forEach(tourPoint => {
        const hasGpsOpen = !!tourPoint?.data?.settings?.gps_open
        const hasGpsLock = !!tourPoint?.data?.settings?.gps_lock
        if (!hasGpsOpen && !hasGpsLock) return

        const pointId = String(tourPoint?.point?.id || tourPoint?.data?.point?.id || tourPoint?.id || '')
        if (!pointId) return

        const point = tourPoint.point || tourPoint.data?.point
        const gpsLocation = point?.gps_location || tourPoint?.data?.gps_location
        if (!gpsLocation) return

        let pointLat, pointLng
        if (typeof gpsLocation === 'string') {
          const coords = gpsLocation.split(',').map(parseFloat)
          pointLat = coords[0]; pointLng = coords[1]
        } else if (gpsLocation && typeof gpsLocation === 'object') {
          pointLat = gpsLocation.latitude || gpsLocation.lat
          pointLng = gpsLocation.longitude || gpsLocation.lng
        }
        if (isNaN(pointLat) || isNaN(pointLng)) return

        const distances = history
          .map(loc => this.haversine(loc, pointLat, pointLng))
          .filter(d => d !== null && !isNaN(d))
        if (distances.length === 0) return
        const avgDistance = distances.reduce((a, b) => a + b, 0) / distances.length

        if (!best || avgDistance < best.avgDistance) {
          const configuredOpenRadius   = tourPoint?.data?.settings?.gps_radius_open   || this.gpsOpenRadius
          const configuredUnlockRadius = tourPoint?.data?.settings?.gps_radius_unlock || this.gpsUnlockRadius
          const acc = this.gpsAccuracy
          const effOpenRadius   = configuredOpenRadius   + Math.min(acc || 0, configuredOpenRadius)
          const effUnlockRadius = configuredUnlockRadius + Math.min(acc || 0, configuredUnlockRadius)

          let unmetConditions = []
          if (gameModule && typeof gameModule.getUnmetConditions === 'function') {
            try { unmetConditions = gameModule.getUnmetConditions(tourPoint, 'unlock') || [] } catch (e) { /**/ }
          }

          const name = showMultiLingualText(tourPoint.data?.title, null, true, this.$i18n?.locale || 'nl') || `point ${pointId}`

          best = {
            pointId, name, avgDistance,
            gpsOpen: hasGpsOpen, gpsLock: hasGpsLock,
            effOpenRadius, effUnlockRadius,
            inAutoOpenRange: avgDistance <= effOpenRadius,
            isLocked: this.gpsLockedPoints instanceof Set ? this.gpsLockedPoints.has(pointId) : false,
            alreadyOpened: this.gpsAutoOpenedPoints instanceof Set ? this.gpsAutoOpenedPoints.has(pointId) : false,
            unmetConditions, gameActive
          }
        }
      })

      return best
    }
  },
  methods: {
    haversine(location, pointLat, pointLng) {
      const lat1 = location?.latitude || location?.lat
      const lon1 = location?.longitude || location?.lng
      if (isNaN(lat1) || isNaN(lon1)) return null
      const R = 6371e3
      const φ1 = lat1 * Math.PI / 180
      const φ2 = pointLat * Math.PI / 180
      const Δφ = (pointLat - lat1) * Math.PI / 180
      const Δλ = (pointLng - lon1) * Math.PI / 180
      const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    },

    describeCondition(condition) {
      if (!condition) return 'unknown'
      const type   = condition.type   || condition.condition_type || ''
      const target = condition.target || condition.variable || condition.point_id || ''
      const value  = condition.value  !== undefined ? condition.value : ''
      return `${type}${target ? ' ' + target : ''}${value !== '' ? ' = ' + value : ''}`
    }
  }
}
</script>
